import { useEffect } from 'react';
import { Box, Flex, Text } from 'rebass';
import { createGlobalStyle } from 'styled-components';

import Message from './components/Message';
import Button from './components/Button';
import Badge from './components/Badge';
import useLocalStorage from './hooks/useLocalStorage';
import CodeBlock from './components/CodeBlock';

import Prism from "./prism";
import PrismDark from './prism-dark';
import PrismLight from './prism-light';

import { palettes, palettesWithoutGray } from './ThemeSwitcher';
import Icon from './components/Icon';

const capitalize = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const lowercase = (string) => {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

const GlobalStyle = createGlobalStyle`
  ${props => props.dark ? PrismDark : PrismLight}
`;

const ExampleContent = () => {
  const useSemanticAlias = true;

  useEffect(() => {
    Prism.highlightAll();
  }, []);

  // console.log(palettesWithoutGray({ alias: useSemanticAlias }));

  return (
    <Box>
      <GlobalStyle />
      <Text as="h2" mt={2} mb={3}>Text</Text>
      <Box flexDirection={['row', 'column']} mb={3}>
        {['neutral', 'neutralMuted', 'neutralSubtle', ...palettesWithoutGray({ alias: true }).filter(p => p !== 'Neutral')].map(color => (
          <Text
            key={color}
            color={`var(--text${capitalize(color)})`}
            mb={2}
            css={`
              cursor: pointer;
              &:hover {
                color: var(--text${capitalize(color)}Hover);
              }
              &:active {
                color: var(--text${capitalize(color)}Active);
              }
            `}
          >Example text ({lowercase(color)})</Text>
        ))}
      </Box>
      <CodeBlock mb={3}>
{`// Usage
import { Text } from 'ComponentLibrary/text';

<Text>Example text</Text>  //  color="neutral"

<Text color="neutralMuted">Example text</Text>
<Text color="neutralSubtle">Example text</Text>

<Text color="primary">Example text</Text>
<Text color="danger">Example text</Text>
<Text color="success">Example text</Text>
<Text color="warning">Example text</Text>
<Text color="orange">Example text</Text>
<Text color="purple">Example text</Text>
`}
      </CodeBlock>
      <CodeBlock mb={5}>
{`// componentLibrary/text/Text.js
import { Text } from 'rebass';

const Text = ({ color, props }) => (
  <Text
    color={\`text\${color}\`} 
    {...props}
  />
);

Text.propTypes = {
  color: PropTypes.oneOf(['neutral', 'neutralMuted', 'neutralSubtle', 'primary', 'danger', 'success', 'warning', 'orange', 'purple']) ,
};

Text.defaultProps = {
  color: 'neutral',
}
`}
      </CodeBlock>
      <Text as="h2" mt={2} mb={3}>Icon</Text>
      <Box flexDirection={['row', 'column']} mb={3}>
        {palettesWithoutGray({ alias: useSemanticAlias }).map(color => (
          <Icon key={color} icon="face-smile-relaxed" color={`var(--icon${capitalize(color)})`} mr={1} />
        ))}
      </Box>
      <CodeBlock  mb={5}>
{`// Usage
import { Icon } from 'ComponentLibrary/icon';

<Icon icon="face-smile-relaxed" />  //  color="neutral"

<Icon icon="face-smile-relaxed" color="primary" />
<Icon icon="face-smile-relaxed" color="danger" />
<Icon icon="face-smile-relaxed" color="success" />
<Icon icon="face-smile-relaxed" color="warning" />
<Icon icon="face-smile-relaxed" color="orange" />
<Icon icon="face-smile-relaxed" color="purple" />
`}
      </CodeBlock>
      <Text as="h2" mt={2} mb={3}>Badge</Text>  
      <Flex flexDirection={['row', 'column']}>
        {['solid', 'muted', 'ghost'].map(variant => (
          <Flex flexWrap="wrap" justifyContent="flex-start" flexDirection={['column', 'row']} key={variant}>
            {palettes({ alias: useSemanticAlias }).map(color => (
              <Badge key={color} icon="check" color={color} variant={variant} mr={2} mb={2}>Badge</Badge>
            ))}
          </Flex>
        ))}
      </Flex>
      <CodeBlock mb={5}>
{`// Usage
import { Badge } from 'ComponentLibrary/text';

<Badge icon="check">Badge</Badge>  //  color="neutral"

<Badge icon="check" color="primary">Badge</Badge>
<Badge icon="check" color="danger">Badge</Badge>
<Badge icon="check" color="success">Badge</Badge>
<Badge icon="check" color="warning">Badge</Badge>
<Badge icon="check" color="orange">Badge</Badge>
<Badge icon="check" color="purple">Badge</Badge>
`}
      </CodeBlock>
      <Text as="h2" mt={2} mb={3}>Button</Text>
      <Flex flexDirection={['row', 'column']}>
        {['solid', 'outline', 'ghost'].map(variant => (
          <Flex flexWrap="wrap" justifyContent="flex-start" flexDirection={['column', 'row']} key={variant}>
            {palettes({ alias: useSemanticAlias }).map(color => (
              <Button key={color} icon="chevron-down" color={color} variant={variant} mr={2} mb={2}>Click me</Button>
            ))}
          </Flex>
        ))}
      </Flex>
      <CodeBlock mb={5}>
{`// Usage
import { Button } from 'ComponentLibrary/button';

<Button>Click me</Button>  //  color="neutral"

<Button color="primary">Click me</Button>
<Button color="danger">Click me</Button>
<Button color="success">Click me</Button>
<Button color="warning">Click me</Button>
<Button color="orange">Click me</Button>
<Button color="purple">Click me</Button>
`}
      </CodeBlock>  
      <Text as="h2" mt={2} mb={3}>Messages</Text>
      {['muted'].map(variant => (
        palettes({ alias: useSemanticAlias }).map(color => (
          <Message
            data-color={color}
            color={color}
            key={color}
            icon="circle-info"
            headingText="Get started"
            variant={variant}
            buttons={(
              <>
                <Button
                  color={variant === 'solid' ? 'white' : color}
                  variant="solid"
                >
                  Get started
                </Button>
                <Button
                  color={color}
                  ml={1}
                  variant={variant === 'solid' ? 'knockoutOutline' : 'outline'}
                >
                  Dismiss
                </Button>              
              </>
            )}
          >
            It's fast, flexible, and reliable — with zero-runtime.
          </Message>
        ))
      ))}
      <CodeBlock mb={5}>
{`// Usage
import { Message } from 'ComponentLibrary/layout';

<Message
  headingText="Get started"
  // color="neutral"
>
  It's fast, flexible, and reliable — with zero-runtime.
</Message>

<Message
  headingText="Get started"
  color="primary"
>
  It's fast, flexible, and reliable — with zero-runtime.
</Message>

<Message
  headingText="Get started"
  color="danger"
>
  It's fast, flexible, and reliable — with zero-runtime.
</Message>

<Message
  headingText="Get started"
  color="success"
>
  It's fast, flexible, and reliable — with zero-runtime.
</Message>

<Message
  headingText="Get started"
  color="warning"
>
  It's fast, flexible, and reliable — with zero-runtime.
</Message>

<Message
  headingText="Get started"
  color="orange"
>
  It's fast, flexible, and reliable — with zero-runtime.
</Message>

<Message
  headingText="Get started"
  color="purple"
>
  It's fast, flexible, and reliable — with zero-runtime.
</Message>
`}
      </CodeBlock>  
    </Box>
  )
}

export default ExampleContent